import { useEffect, useState } from 'react';
import { Form, Link, redirect, useActionData, useLoaderData, useNavigation } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { ApiError, api } from '../lib/api';
import type { StudioIdentity } from '../lib/types';
import GoogleSignIn from '../components/GoogleSignIn';
import FormError from '../components/FormError';
import AuthScene from '../components/AuthScene';
import AuthWelcome from '../components/AuthWelcome';
import PasswordField from '../components/PasswordField';
import type { AuthState, MfaChallengeResponse } from '../lib/types';

interface SignInLoaderData {
  auth: AuthState;
  next: string;
  signedOut: boolean;
}

interface SignInActionData {
  error?: string;
  mfaChallenge?: string;
}

const safeNext = (value: string | null): string => (value && /^\/(?!\/)[A-Za-z0-9/_?=&.-]*$/.test(value) ? value : '/');

export async function signInLoader({ request }: LoaderFunctionArgs): Promise<SignInLoaderData | Response> {
  const auth = await api.authState();
  const params = new URL(request.url).searchParams;
  const next = safeNext(params.get('next'));
  if (auth.authenticated) return redirect(next);
  if (auth.needsSetup) return redirect('/signup');
  return { auth, next, signedOut: params.has('signedout') };
}

export async function signInAction({ request }: ActionFunctionArgs): Promise<SignInActionData | Response> {
  const form = await request.formData();
  const intent = String(form.get('intent') || 'password');
  const next = safeNext(String(form.get('next') || '/'));

  try {
    if (intent === 'mfa') {
      await api.mfaLogin({ challenge: String(form.get('challenge') || ''), code: String(form.get('code') || '') });
      return redirect(next);
    }

    if (intent === 'sso') {
      const result = await api.startSso({ email: String(form.get('email') || ''), returnPath: next });
      window.location.assign(result.authorizationUrl);
      return {};
    }

    const result = await api.login({
      email: String(form.get('email') || ''),
      password: String(form.get('password') || '')
    });
    if ((result as MfaChallengeResponse)?.mfaRequired) {
      return { mfaChallenge: (result as MfaChallengeResponse).challenge };
    }
    return redirect(next);
  } catch (error) {
    return { error: error instanceof ApiError || error instanceof Error ? error.message : 'Sign in failed.' };
  }
}

export default function SignIn() {
  const { auth, next, signedOut } = useLoaderData() as SignInLoaderData;
  const actionData = useActionData() as SignInActionData | undefined;
  const navigation = useNavigation();
  const [identity, setIdentity] = useState<StudioIdentity | null>(null);
  const [created, setCreated] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const busy = navigation.state !== 'idle';
  const intent = navigation.formData?.get('intent');

  useEffect(() => {
    if (actionData?.error) setEmailOpen(true);
  }, [actionData]);

  if (identity) {
    return (
      <AuthScene
        eyebrow="Camera rolling"
        headline={['The floor', 'is open.']}
        ghost="Welcome back."
        lede="Your productions, your crew and every locked frame are exactly where you left them."
      >
        <AuthWelcome identity={identity} next={created ? '/welcome' : next} greeting={created ? 'Welcome aboard' : 'Welcome back'} />
      </AuthScene>
    );
  }

  if (actionData?.mfaChallenge) {
    return (
      <AuthScene
        eyebrow="Second factor"
        headline={['Nothing', 'moves']}
        ghost="unverified."
        lede="Your studio holds locked frames, approved cuts and live provider credit. One more step before the floor opens."
      >
        <div className="sa-view">
          <span className="eyebrow">Protected account</span>
          <h1>Enter authentication code</h1>
          <p className="auth-sub">Use the six-digit code from your authenticator app, or one unused recovery code.</p>
          <Form method="post" className="auth-form" replace>
            <FormError message={actionData.error} />
            <input type="hidden" name="intent" value="mfa" />
            <input type="hidden" name="challenge" value={actionData.mfaChallenge} />
            <input type="hidden" name="next" value={next} />
            <label className="field">
              <span>Authentication or recovery code</span>
              <input className="input mfa-code-input" name="code" required autoComplete="one-time-code" inputMode="numeric" autoFocus />
            </label>
            <button className="app-button primary" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify and sign in'}
            </button>
            <Link className="app-button ghost" to="/signin">
              Use another account
            </Link>
          </Form>
        </div>
      </AuthScene>
    );
  }

  return (
    <AuthScene
      eyebrow="AI cinema studio"
      headline={['Run a', 'production.']}
      ghost="Not a prompt."
      lede="One workspace, a trained crew, and a router that already knows which model fits the shot. You confirm the spend. Sit down and finish the film."
    >
      <div className="sa-view">
        <span className="eyebrow">{signedOut ? 'Signed out' : 'Welcome back'}</span>
        <h1>{signedOut ? 'That’s a wrap. See you on set.' : 'Sign in to your studio'}</h1>
        <p className="auth-sub">
          {signedOut
            ? 'You are signed out on every device. Pick up where you left off whenever you are ready.'
            : 'Your Google account is your studio pass. No password to remember.'}
        </p>

        <GoogleSignIn
          mode="signin"
          autoPrompt={!signedOut}
          onSignedIn={(value, isNew) => {
            setGoogleError('');
            setCreated(isNew);
            setIdentity(value);
          }}
          onError={setGoogleError}
        />
        {googleError ? (
          <div className="sa-error-slot">
            <FormError message={googleError} />
          </div>
        ) : null}
        <p className="sa-trust">
          <span>Name, email and picture only</span>
          <span>Verified by Google</span>
        </p>

        <div className="sa-disclosure">
          <button type="button" className="sa-toggle" aria-expanded={emailOpen} aria-controls="signin-email" onClick={() => setEmailOpen(open => !open)}>
            <span>
              Email &amp; SSO <i aria-hidden="true">+</i>
            </span>
          </button>
          <div id="signin-email" className="sa-collapse" data-open={emailOpen || undefined}>
            <div>
              <Form method="post" className="auth-form" replace inert={!emailOpen || undefined}>
                <FormError message={actionData?.error} />
                <input type="hidden" name="next" value={next} />
                <label className="field">
                  <span>Email</span>
                  <input className="input" type="email" name="email" required autoComplete="email" />
                </label>
                <PasswordField label="Password" name="password" autoComplete="current-password" />
                <button className="app-button primary" name="intent" value="password" disabled={busy}>
                  {busy && intent === 'password' ? 'Signing in…' : 'Sign in'}
                </button>
                <button className="app-button ghost" type="submit" name="intent" value="sso" formNoValidate disabled={busy}>
                  {busy && intent === 'sso' ? 'Finding your provider…' : 'Continue with SSO'}
                </button>
                <p className="field-note">SSO uses the email above to find your identity provider.</p>
              </Form>
            </div>
          </div>
        </div>

        <p className="auth-alt sa-footer">
          {auth.needsSetup ? (
            <Link to="/signup">Create the studio owner account</Link>
          ) : (
            <>
              New here? <Link to="/signup">Create an account</Link>
            </>
          )}
        </p>
      </div>
    </AuthScene>
  );
}
